import React, { useState, useCallback, useEffect } from 'react';
import { useGoogleMaps } from '../hooks/useGoogleMaps';
import { useFareCalculation } from '../hooks/useFareCalculation';
import { useMapInteraction } from '../hooks/useMapInteraction';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { LocationCoords, FareRequest } from '../types/fare';
import { isWithinSriLanka } from '../services/fareService';
import { checkBackendHealth } from '../services/fareService';
import LocationInputPanel from './fare/LocationInputPanel';
import FareMapContainer from './fare/FareMapContainer';
import FareResultsPanel from './fare/FareResultsPanel';
import BottomSheet from './fare/BottomSheet';
import FloatingLocationInput from './fare/FloatingLocationInput';
import RegistrationPrompt from './RegistrationPrompt';
import { Loader2, AlertCircle } from 'lucide-react';

export default function Services() {
  const { authState } = useAuth();
  const userId = authState.user?.userId || null;
  const { tier, canCalculateFare, recordFareUsage, checkFareLimitOnServer } = usePermissions();
  const { isLoaded, loadError } = useGoogleMaps();
  const { loading, fareData, error, calculate, reset } = useFareCalculation(userId);
  const {
    selectionMode,
    pickupMarker,
    destinationMarker,
    selectedRouteIndex,
    startPickupSelection,
    startDestinationSelection,
    cancelSelection,
    handleMapClick,
    clearMarkers,
    setPickup,
    setDestination,
    setSelectedRouteIndex,
  } = useMapInteraction();

  const [pickupAddress, setPickupAddress] = useState('');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [showMobileInput, setShowMobileInput] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showRegistrationPrompt, setShowRegistrationPrompt] = useState(false);
  const [registrationVariant, setRegistrationVariant] = useState<'google_auth' | 'whatsapp_verify'>('google_auth');
  const [rateLimitWaitMinutes, setRateLimitWaitMinutes] = useState<number | undefined>(undefined);
  const [backendAvailable, setBackendAvailable] = useState<boolean | null>(null);
  const [checkingBackend, setCheckingBackend] = useState(true);
  const [polylinesKey, setPolylinesKey] = useState(Date.now()); // Force polylines cleanup without reloading map
  const [clearingRoutes, setClearingRoutes] = useState(false); // Block route rendering during clear
  
  // Detect if we're on mobile or desktop (use window width)
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  // Check backend availability with retries
  const checkBackend = useCallback(async (retries = 3, delay = 2000) => {
    setCheckingBackend(true);
    
    for (let i = 0; i < retries; i++) {
      const isAvailable = await checkBackendHealth();
      
      if (isAvailable) {
        setBackendAvailable(true);
        setCheckingBackend(false);
        return;
      }
      
      // Wait before next retry (except on last attempt)
      if (i < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    // All retries failed
    setBackendAvailable(false);
    setCheckingBackend(false);
  }, []);

  // Check backend on mount
  useEffect(() => {
    checkBackend();
  }, [checkBackend]);

  // Handle window resize for responsive map rendering
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePickupSelect = useCallback(
    (location: LocationCoords, address: string) => {
      if (!isWithinSriLanka(location.lat, location.lng)) {
        alert('Pickup location must be within Sri Lanka');
        return;
      }
      setPickup(location);
      setPickupAddress(address);
    },
    [setPickup]
  );

  const handleDestinationSelect = useCallback(
    (location: LocationCoords, address: string) => {
      if (!isWithinSriLanka(location.lat, location.lng)) {
        alert('Destination must be within Sri Lanka');
        return;
      }
      setDestination(location);
      setDestinationAddress(address);
    },
    [setDestination]
  );

  const handleMapClickEvent = useCallback(
    (location: LocationCoords) => {
      const result = handleMapClick(location);
      if (result === 'pickup') {
        handlePickupSelect(location, `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`);
      } else if (result === 'destination') {
        handleDestinationSelect(location, `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`);
      }
    },
    [handleMapClick, handlePickupSelect, handleDestinationSelect]
  );

  const handleCalculate = useCallback(
    async (
      pickup: LocationCoords,
      destination: LocationCoords | null,
      manualDistance: number | null,
      pickupAddr: string,
      destAddr: string,
      isQuickRoute: boolean = false
    ) => {
      // Quick routes are FREE for everyone (including unregistered users)
      if (isQuickRoute) {
      } else {
        // Check client-side permissions first
        const farePermission = canCalculateFare(isQuickRoute);
        
        if (!farePermission.allowed) {
          if (tier === 'unregistered') {
            setRegistrationVariant('google_auth');
            setShowRegistrationPrompt(true);
          } else {
            // Google auth user hit limit (shouldn't happen on client-side, but handle it)
            setRegistrationVariant('whatsapp_verify');
            setShowRegistrationPrompt(true);
          }
          return;
        }

        // For registered users (google_auth or whatsapp_verified with +94), check server
        if (farePermission.needsServerCheck && destination) {
          const serverPermission = await checkFareLimitOnServer(pickup, destination, isQuickRoute);
          
          if (!serverPermission.allowed) {
            setRegistrationVariant('whatsapp_verify');
            setRateLimitWaitMinutes(serverPermission.waitMinutes);
            setShowRegistrationPrompt(true);
            return;
          }
        }
      }
      
      // Build request
      const request: FareRequest = {
        start: pickup,
        inputMethod: destination ? 'pin' : 'manual',
      };

      if (destination) {
        request.end = destination;
      } else if (manualDistance) {
        request.distanceKm = manualDistance;
      }

      try {
        await calculate(request);
        
        // Show results immediately for instant feedback
        setShowResults(true);
        setShowMobileInput(false);
        
        // Record usage in background (non-blocking) - but NOT for quick routes (they're free!)
        if (!isQuickRoute) {
          // Pass pickup and destination coordinates for backend recording
          // Don't await - let it run in background so UI isn't blocked
          recordFareUsage(pickup, destination).catch(() => {
            // Error recording fare usage (non-critical)
          });
        }
      } catch (err) {
        // Error is handled by useFareCalculation hook
        // Show results sheet even on error so user can see the error message
        setShowResults(true);
        setShowMobileInput(false);
      }
    },
    [calculate, canCalculateFare, checkFareLimitOnServer, tier, recordFareUsage]
  );

  const handleMobileCalculate = useCallback(() => {
    if (pickupMarker) {
      handleCalculate(
        pickupMarker,
        destinationMarker,
        null,
        pickupAddress,
        destinationAddress
      );
    }
  }, [pickupMarker, destinationMarker, pickupAddress, destinationAddress, handleCalculate]);

  const handleStartPinSelection = useCallback(
    (type: 'pickup' | 'destination') => {
      if (type === 'pickup') {
        startPickupSelection();
      } else {
        startDestinationSelection();
      }
      setShowMobileInput(false);
    },
    [startPickupSelection, startDestinationSelection]
  );

  const handleReset = useCallback(() => {
    // Set clearing flag to block route rendering
    setClearingRoutes(true);
    
    // Clear all state immediately
    reset();
    clearMarkers();
    setPickupAddress('');
    setDestinationAddress('');
    setSelectedRouteIndex(0);
    setShowResults(false);
    
    // Re-check backend health on reset if it was unavailable
    if (backendAvailable === false) {
      checkBackend();
    }
    
    // Force unmount with key change and unblock rendering after cleanup
    setTimeout(() => {
      const newKey = Date.now(); // Use timestamp for unique key
      setPolylinesKey(newKey);
      
      setTimeout(() => {
        setClearingRoutes(false);
      }, 50);
    }, 100);
  }, [reset, clearMarkers, setSelectedRouteIndex, backendAvailable, checkBackend]);

  const handleUseCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const location = { lat: latitude, lng: longitude };
        
        // Set the pickup marker
        setPickup(location);
        
        // Reverse geocode to get address
        try {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location }, (results, status) => {
            if (status === 'OK' && results && results[0]) {
              setPickupAddress(results[0].formatted_address);
            } else {
              setPickupAddress(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
            }
          });
        } catch (error) {
          setPickupAddress(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
        }
        
        // Exit selection mode
        cancelSelection();
      },
      (error) => {
        alert('Unable to get your current location. Please check your browser permissions.');
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  }, [setPickup, cancelSelection]);

  // Google Maps loading state
  if (loadError) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md text-center">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Maps</h2>
          <p className="text-gray-600">
            Failed to load Google Maps. Please check your internet connection and refresh the page.
          </p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 size={48} className="text-green-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading Maps...</p>
        </div>
      </div>
    );
  }

  // Backend health check loading
  if (checkingBackend) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md text-center">
          <Loader2 size={48} className="text-green-500 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Connecting to Fare Validator</h2>
          <p className="text-gray-600">
            Checking backend availability...
          </p>
        </div>
      </div>
    );
  }

  // Backend health check warning
  if (backendAvailable === false) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md text-center">
          <AlertCircle size={48} className="text-orange-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Fare Validator Backend Unavailable</h2>
          <p className="text-gray-600 mb-4">
            The fare validator backend is not running on port 5002.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Please start the fare-validator backend service.
          </p>
          <button
            onClick={() => checkBackend()}
            className="px-6 py-3 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors inline-flex items-center gap-2"
          >
            <Loader2 size={20} />
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-hidden">
      {/* Desktop Layout */}
      <div className="hidden md:grid md:grid-cols-[35%_1fr] h-full">
        {/* Left Sidebar */}
        <div className="bg-white shadow-xl overflow-hidden flex flex-col">
          {/* Combined scrollable section - Inputs and Results */}
          <div className="flex-1 overflow-y-auto min-h-0 flex flex-col">
          {/* Inputs Section */}
            <div className="flex-shrink-0">
            <LocationInputPanel
              onCalculate={handleCalculate}
              onPickupSelect={handlePickupSelect}
              onDestinationSelect={handleDestinationSelect}
              onStartPinSelection={handleStartPinSelection}
              onReset={handleReset}
              loading={loading}
              pickupLocation={pickupMarker}
              destinationLocation={destinationMarker}
              pickupAddress={pickupAddress}
              destinationAddress={destinationAddress}
            />
          </div>

            {/* Results Section - Desktop - Scrolls together with inputs */}
          {(fareData || loading || error) && (
              <div className="flex-shrink-0 p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Results</h2>
                {fareData && (
                  <button
                    onClick={handleReset}
                    className="text-sm text-gray-600 hover:text-gray-900"
                  >
                    Clear
                  </button>
                )}
              </div>
              <FareResultsPanel
                fareData={fareData}
                loading={loading}
                error={error}
                selectedRouteIndex={selectedRouteIndex}
                onRouteSelect={setSelectedRouteIndex}
              />
            </div>
          )}
          </div>
        </div>

        {/* Right Map - Only render on desktop */}
        <div className="relative">
          {!isMobile && (
            <FareMapContainer
              pickupLocation={pickupMarker}
              destinationLocation={destinationMarker}
              fareData={fareData}
              selectedRouteIndex={selectedRouteIndex}
              onRouteSelect={setSelectedRouteIndex}
              selectionMode={selectionMode}
              onMapClick={handleMapClickEvent}
              polylinesKey={polylinesKey}
              clearingRoutes={clearingRoutes}
              mapId="DESKTOP"
            />
          )}
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden h-full relative">
        {/* Full-screen Map - Only render on mobile */}
        {isMobile && (
          <FareMapContainer
            pickupLocation={pickupMarker}
            destinationLocation={destinationMarker}
            fareData={fareData}
            selectedRouteIndex={selectedRouteIndex}
            onRouteSelect={setSelectedRouteIndex}
            selectionMode={selectionMode}
            onMapClick={handleMapClickEvent}
            polylinesKey={polylinesKey}
            clearingRoutes={clearingRoutes}
            mapId="MOBILE"
          />
        )}

        {/* Floating Input Card - Always visible when no results */}
        {!showResults && (
          <FloatingLocationInput
            pickupAddress={pickupAddress}
            destinationAddress={destinationAddress}
            onCalculate={handleMobileCalculate}
            onPickupClick={() => startPickupSelection()}
            onDestinationClick={() => startDestinationSelection()}
            onCancelSelection={cancelSelection}
            onUseCurrentLocation={handleUseCurrentLocation}
            loading={loading}
            hasValidInputs={!!pickupMarker && !!destinationMarker}
            selectionMode={selectionMode}
          />
        )}

        {/* Results Bottom Sheet - Only show after calculation with data/error */}
        {showResults && (fareData || error) && (
          <BottomSheet
            isOpen={showResults}
            onClose={() => setShowResults(false)}
            onClear={() => {
              handleReset();
              setShowResults(false);
            }}
            initialState="half"
          >
            {(sheetState) => (
              <FareResultsPanel
                fareData={fareData}
                loading={loading}
                error={error}
                selectedRouteIndex={selectedRouteIndex}
                onRouteSelect={setSelectedRouteIndex}
                sheetState={sheetState}
              />
            )}
          </BottomSheet>
        )}

        {/* Registration Prompt */}
        <RegistrationPrompt
          isOpen={showRegistrationPrompt}
          onClose={() => {
            setShowRegistrationPrompt(false);
            setRateLimitWaitMinutes(undefined);
          }}
          variant={registrationVariant}
          trigger="fare_limit"
          waitMinutes={rateLimitWaitMinutes}
        />
      </div>
    </div>
  );
}
